import {
  Headphones,
  BarChart3,
  ShieldCheck,
  Workflow,
  Phone,
  Gauge,
  Lock,
  Code,
  Clock,
  Rocket,
} from 'lucide-react';
import { useBranding } from '@/features/auth/api';
import './auth-layout.css';

// FontAwesome 4 — only used for the globe watermark on the login page
const FA_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css';

interface AuthLayoutProps {
  title: string;
  description?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export function AuthLayout({ title, description, footer, children }: AuthLayoutProps) {
  const branding = useBranding();
  const company = branding.data?.company ?? 'OBD3';

  return (
    <div className="grid h-screen w-full lg:grid-cols-[1.1fr_1fr]">
      {/* ---- Left hero ---- */}
      <aside
        className="auth-hero-panel relative hidden flex-col justify-between overflow-hidden lg:flex"
        style={{
          background: 'linear-gradient(160deg, #060b16 0%, #0d1830 55%, #141f38 100%)',
          padding: 'clamp(1.1rem, 3vh, 3rem) clamp(2rem, 4.5vw, 3.5rem)',
          color: '#fff',
        }}
      >
        {/* ═══ Animated background layers ═══ */}
        <span className="brand-glow glow-1" />
        <span className="brand-glow glow-2" />
        <span className="brand-glow glow-3" />
        <span className="brand-stars stars-1" />
        <span className="brand-stars stars-2" />
        <span className="brand-stars stars-3" />
        <span className="brand-grid" />
        <span className="brand-watermark-glow" />
        <span className="brand-orbit orbit-1" />
        <span className="brand-orbit orbit-2" />
        <span className="brand-orbit-spin spin-1">
          <span className="brand-satellite" />
        </span>
        <span className="brand-orbit-spin spin-2">
          <span className="brand-satellite sat-purple" />
        </span>

        {/* FontAwesome CSS for globe icon */}
        <link rel="stylesheet" href={FA_CSS} />

        {/* Globe watermark — same fa-globe as old OBD */}
        <i className="fa fa-globe brand-watermark" aria-hidden="true" />

        {/* ═══ Content ═══ */}

        {/* Brand row */}
        <div className="relative z-10 flex items-center gap-3">
          {branding.data?.logoUrl ? (
            <img src={branding.data.logoUrl} alt={company} className="h-9 w-auto" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-white/10 ring-1 ring-white/15">
              <Phone className="h-[18px] w-[18px] text-white" />
            </div>
          )}
          <span className="text-[1.1rem] font-bold tracking-wide">{company} Platform</span>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 max-w-[460px]">
          <h1
            className="font-bold leading-[1.22] tracking-tight text-white"
            style={{ fontSize: 'clamp(1.5rem, 4.2vh, 2.8rem)' }}
          >
            The complete{' '}
            <span className="brand-accent-text">outbound dialer</span>{' '}
            platform.
          </h1>
          <p
            className="mt-2 font-normal text-white/60"
            style={{ fontSize: 'clamp(0.88rem, 2.1vh, 1.2rem)', lineHeight: 1.45 }}
          >
            Powerful, scalable and enterprise-ready outbound calling
            — from one unified control room.
          </p>
        </div>

        {/* Feature grid (2 columns) */}
        <div className="brand-feature-grid relative z-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="brand-feature-item">
              <div className={`brand-feature-icon ${f.iconClass}`}>
                <f.icon className="h-4 w-4" />
              </div>
              <div className="brand-feature-text">
                <span className="brand-feature-title">{f.title}</span>
                <span className="brand-feature-desc">{f.desc}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom benefits row (5 columns) */}
        <div className="brand-benefits-row relative z-10">
          {BENEFITS.map((b) => (
            <div key={b.title} className="brand-benefit-item">
              <b.icon className={`brand-benefit-icon ${b.iconClass}`} style={{ width: 18, height: 18 }} />
              <span className="brand-benefit-title">{b.title}</span>
              <span className="brand-benefit-desc">{b.desc}</span>
            </div>
          ))}
        </div>
      </aside>

      {/* ---- Right form panel (untouched) ---- */}
      <main className="relative flex min-h-0 flex-col items-center justify-center overflow-y-auto bg-background px-6 py-12 sm:px-12">
        <div className="absolute left-6 top-6 flex items-center gap-2 text-foreground lg:hidden">
          {branding.data?.logoUrl ? (
            <img src={branding.data.logoUrl} alt={company} className="h-7 w-auto" />
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <div className="h-3 w-3 rounded-sm bg-primary-foreground" />
            </div>
          )}
          <span className="text-sm font-semibold tracking-tight">{company}</span>
        </div>

        <div className="w-full max-w-md">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {title}
            </h2>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>

          <div className="mt-8 space-y-6">{children}</div>

          {footer && (
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {footer}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const FEATURES = [
  {
    icon: Rocket,
    title: '2Cr+ outbound calls per hour',
    desc: 'Massive scale. Unmatched dialing capacity.',
    iconClass: 'icon-blue',
  },
  {
    icon: Gauge,
    title: 'Predictive & progressive dialing',
    desc: 'Higher connect rate, better productivity.',
    iconClass: 'icon-purple',
  },
  {
    icon: Headphones,
    title: 'Multi-level IVR & multilingual TTS',
    desc: 'Complex IVR flows with natural voice in multiple languages.',
    iconClass: 'icon-green',
  },
  {
    icon: ShieldCheck,
    title: 'Built-in compliance checks',
    desc: 'Stay compliant. Build trust. Protect your brand.',
    iconClass: 'icon-amber',
  },
  {
    icon: BarChart3,
    title: 'Real-time CDR, analytics & call monitoring',
    desc: 'Live dashboards, actionable insights and complete call visibility.',
    iconClass: 'icon-purple',
  },
  {
    icon: Workflow,
    title: 'Visual IVR & campaign flow builder',
    desc: 'Drag, drop and deploy campaign flows in minutes.',
    iconClass: 'icon-blue',
  },
];

const BENEFITS = [
  { icon: Rocket, title: 'Enterprise Ready', desc: 'High availability, fault tolerant & horizontally scalable', iconClass: 'benefit-icon-red' },
  { icon: Lock, title: 'Banking Grade Security', desc: 'Secure infrastructure and data protection', iconClass: 'benefit-icon-blue' },
  { icon: ShieldCheck, title: 'End-to-End Encrypted', desc: 'All data and calls encrypted in transit and at rest', iconClass: 'benefit-icon-green' },
  { icon: Code, title: 'API First Platform', desc: 'Powerful APIs for seamless integrations and automation', iconClass: 'benefit-icon-amber' },
  { icon: Clock, title: '24×7 Support', desc: 'Expert support whenever you need us.', iconClass: 'benefit-icon-purple' },
];